import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaPills, FaChevronDown, FaChevronUp, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { goReserve, goBackSmart } from '../../utils/navigation';

interface FaqPillLowDoseProps {
  ipAllowed: boolean;
}

type QA = { q: string; a: string | string[] };

type SectionBasic = { title: string; paras?: string[] };
type Mechanism = { title: string; points?: string[]; note?: string };
type Formulations = {
  title: string;
  tri?: { subtitle: string; desc?: string; bullets?: string[] };
  mar?: { subtitle: string; desc?: string; bullets?: string[] };
  foot?: string;
};
type KeyCards = { title: string; items: Array<{ k: string; v: string[]; note?: string }> };
type HowTo = { title: string; steps?: string[]; notes?: string[] };
type PeriodShift = {
  title: string;
  tri?: { subtitle: string; delay?: string[]; early?: string[] };
  mar?: { subtitle: string; delay?: string[]; early?: string[] };
};
type Missed = { title: string; rules?: string[] };
type Merits = { title: string; items?: string[] };

type PageMeta = {
  title?: string;
  back?: string;
  cta_menu?: string;
  cta_reserve?: string;
  disclaimer?: string;
};

type PillLowDoseDoc = {
  page?: PageMeta;
  overview?: SectionBasic;
  mechanism?: Mechanism;
  formulations?: Formulations;
  key_cards?: KeyCards;
  howto?: HowTo;
  period_shift?: PeriodShift;
  missed?: Missed;
  merits?: Merits;
  qa?: QA[];
};

type FaqFile = {
  faq?: {
    pill_low_dose?: PillLowDoseDoc;
  };
};

/** ---------- 公開パス補助（Vite 子パス対応） ---------- */
const dataUrl = (filename: string) => {
  // eslint-disable-next-line
  const base = process.env.PUBLIC_URL || '/';
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}data/${filename}`;
};

/** ---------- /public/data/faq_*.json 読み込み ---------- */
async function loadPillLowDose(lang: 'ja' | 'zh'): Promise<PillLowDoseDoc | null> {
  try {
    const res = await fetch(dataUrl(`faq_${lang}.json`), { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json()) as FaqFile;
    return json?.faq?.pill_low_dose || null;
  } catch {
    return null;
  }
}

const FaqPillLowDose: React.FC<FaqPillLowDoseProps> = ({ ipAllowed }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang: 'ja' | 'zh' = (i18n.language || 'ja').startsWith('zh') ? 'zh' : 'ja';

  // 固定順序 Hooks
  const [doc, setDoc] = useState<PillLowDoseDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const toggle = (idx: number) => setOpenIdx((cur) => (cur === idx ? null : idx));

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      const data = await loadPillLowDose(lang);
      if (!mounted) return;
      setDoc(data);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [lang]);

  // doc 变化时默认展开第一条
  useEffect(() => {
    setOpenIdx(0);
  }, [doc]);

  const page: Required<PageMeta> = {
    title: doc?.page?.title || (lang === 'ja' ? '低用量ピルについてのFAQ' : '低剂量口服避孕药常见问题'),
    back: doc?.page?.back || (lang === 'ja' ? '戻る' : '返回'),
    cta_menu: doc?.page?.cta_menu || (lang === 'ja' ? 'ピル（低用量）メニューへ' : '前往低剂量口服避孕药菜单'),
    cta_reserve: doc?.page?.cta_reserve || (lang === 'ja' ? 'オンラインで予約する' : '在线预约'),
    disclaimer:
      doc?.page?.disclaimer ||
      (lang === 'ja'
        ? '※ 本ページの内容は一般的な情報であり、医学的助言ではありません。ご不安がある場合は医師にご相談ください。'
        : '※ 本页面内容仅为一般信息，非个体化医疗建议。如有疑问请咨询医师。'),
  };

  const OVERVIEW = doc?.overview || { title: '', paras: [] };
  const MECHANISM = doc?.mechanism || { title: '', points: [], note: '' };
  const FORMULATIONS = doc?.formulations || {
    title: '',
    tri: { subtitle: '', bullets: [] },
    mar: { subtitle: '', bullets: [] },
    foot: '',
  };
  const KEY_CARDS = doc?.key_cards || { title: '', items: [] };
  const HOWTO = doc?.howto || { title: '', steps: [], notes: [] };
  const PERIOD_SHIFT = doc?.period_shift || {
    title: '',
    tri: { subtitle: '', delay: [], early: [] },
    mar: { subtitle: '', delay: [], early: [] },
  };
  const MISSED = doc?.missed || { title: '', rules: [] };
  const MERITS = doc?.merits || { title: '', items: [] };

  const QA_LIST: QA[] = useMemo(() => (Array.isArray(doc?.qa) ? (doc!.qa as QA[]) : []), [doc]);

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: QA_LIST.slice(0, 25).map((qa) => ({
        '@type': 'Question',
        name: qa.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: Array.isArray(qa.a) ? (qa.a as string[]).join('\n') : (qa.a as string),
        },
      })),
    }),
    [QA_LIST]
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-sky-50/70 font-sans">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-7 md:p-9">
            <h1 className="text-2xl font-semibold text-slate-900">
              {lang === 'ja' ? '読み込み中…' : '加载中…'}
            </h1>
          </div>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-sky-50/70 font-sans">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-white border border-amber-200 rounded-3xl shadow-sm p-7 md:p-9">
            <h1 className="text-2xl font-semibold text-amber-800">
              {lang === 'ja' ? 'データが見つかりません' : '未找到数据'}
            </h1>
            <p className="mt-2 text-amber-700">
              {lang === 'ja'
                ? 'public/data/faq_ja.json / faq_zh.json に faq.pill_low_dose ブロックを配置してください。'
                : '请在 public/data/faq_ja.json / faq_zh.json 中提供 faq.pill_low_dose 区块。'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 bg-sky-50/70 font-sans" translate="no">
      <Helmet>
        <title>{page.title} - Melodis Clinic</title>
        <meta name="robots" content="noindex, nofollow" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 max-w-5xl">
        <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 md:p-9">
          {/* Back */}
          <div className="mb-3">
            <button
              type="button" onClick={() => goBackSmart(navigate, '/faq')}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              <FaArrowLeft className="w-4 h-4" />
              {page.back}
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-rose-100 p-3">
              <FaPills className="text-rose-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              <span className="text-rose-400 mr-2">│</span>
              {page.title}
            </h1>
          </div>

          {/* Overview */}
          <section className="rounded-2xl border border-rose-100 bg-rose-50 p-6 md:p-7 mb-8">
            <h2 className="text-2xl font-semibold text-rose-700 mb-3">{OVERVIEW.title}</h2>
            <div className="space-y-3 text-slate-800 leading-relaxed">
              {(OVERVIEW.paras || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>

          {/* Mechanism */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">{MECHANISM.title}</h3>
            <ul className="list-disc pl-5 space-y-1 text-slate-800">
              {(MECHANISM.points || []).map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
            {MECHANISM.note ? <p className="text-slate-700 mt-3">{MECHANISM.note}</p> : null}
          </section>

          {/* Formulations */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{FORMULATIONS.title}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">{FORMULATIONS.tri?.subtitle}</div>
                {FORMULATIONS.tri?.desc ? <p className="text-slate-700 mb-2">{FORMULATIONS.tri.desc}</p> : null}
                <ul className="list-disc pl-5 space-y-1 text-slate-800">
                  {(FORMULATIONS.tri?.bullets || []).map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">{FORMULATIONS.mar?.subtitle}</div>
                {FORMULATIONS.mar?.desc ? <p className="text-slate-700 mb-2">{FORMULATIONS.mar.desc}</p> : null}
                <ul className="list-disc pl-5 space-y-1 text-slate-800">
                  {(FORMULATIONS.mar?.bullets || []).map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>
            </div>
            {FORMULATIONS.foot ? <p className="text-slate-700 mt-3">{FORMULATIONS.foot}</p> : null}
          </section>

          {/* Key Cards */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{(doc?.key_cards?.title) || ''}</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {(KEY_CARDS.items || []).map((card, idx) => (
                <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="font-semibold text-slate-900 mb-1">{card.k}</div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                    {card.v.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                  {card.note ? <p className="text-slate-600 text-xs mt-2">{card.note}</p> : null}
                </div>
              ))}
            </div>
          </section>

          {/* HowTo / Missed / Period Shift */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{lang === 'ja' ? '服用ガイド' : '用药指南'}</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">{HOWTO.title}</div>
                <ol className="list-decimal pl-5 space-y-1 text-slate-800 text-sm">
                  {(HOWTO.steps || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
                <ul className="list-disc pl-5 space-y-1 text-slate-600 text-xs mt-2">
                  {(HOWTO.notes || []).map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">{MISSED.title}</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                  {(MISSED.rules || []).map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 mt-4">
              <div className="font-semibold text-slate-900 mb-1">{PERIOD_SHIFT.title}</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <div className="font-medium text-slate-900">{PERIOD_SHIFT.tri?.subtitle}</div>
                  <div className="text-slate-800 text-sm mt-1">{lang === 'ja' ? '【遅らせる】' : '【推迟】'}</div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                    {(PERIOD_SHIFT.tri?.delay || []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                  <div className="text-slate-800 text-sm mt-2">{lang === 'ja' ? '【早める】' : '【提前】'}</div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                    {(PERIOD_SHIFT.tri?.early || []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="font-medium text-slate-900">{PERIOD_SHIFT.mar?.subtitle}</div>
                  <div className="text-slate-800 text-sm mt-1">{lang === 'ja' ? '【遅らせる】' : '【推迟】'}</div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                    {(PERIOD_SHIFT.mar?.delay || []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                  <div className="text-slate-800 text-sm mt-2">{lang === 'ja' ? '【早める】' : '【提前】'}</div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                    {(PERIOD_SHIFT.mar?.early || []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Q&A 折りたたみ（首条默认展开，可关闭） */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              {lang === 'zh' ? '常见问题' : 'よくある質問'}
            </h3>
            <div className="space-y-3">
              {QA_LIST.map((qa, idx) => {
                const isOpen = openIdx === idx;
                return (
                  <div key={idx} className="border border-slate-200 rounded-xl bg-slate-50">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggle(idx)}
                      className="w-full flex items-start justify-between gap-4 px-4 py-3 text-left"
                    >
                      <span className="font-medium text-slate-900">{qa.q}</span>
                      {isOpen ? (
                        <FaChevronUp className="mt-1 shrink-0 text-slate-400" />
                      ) : (
                        <FaChevronDown className="mt-1 shrink-0 text-slate-400" />
                      )}
                    </button>
                    <div className={`px-4 pb-4 ${isOpen ? '' : 'hidden'}`}>
                      {Array.isArray(qa.a) ? (
                        <ul className="list-disc pl-5 space-y-1 text-slate-800">
                          {(qa.a as string[]).map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-slate-800">{qa.a as string}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-slate-500 text-xs mt-4">{page.disclaimer}</p>
          </section>

          {/* CTA */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/menu/pill/low-dose')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-slate-700 hover:bg-slate-50"
            >
              {page.cta_menu}
            </button>
            <button
              disabled
              className="inline-block bg-gray-400 text-white px-8 py-3 rounded-full font-semibold shadow-sm cursor-not-allowed grayscale"
            >
              {lang === 'ja' ? '準備中' : '筹备中'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqPillLowDose;
